import { Calendar, Clock, Plus } from "lucide-react";
import type { ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import type {
  TaskGroup,
  useCreateMilestoneAnalysisTask,
} from "@/hooks/api/use-milestone-analyses";
import { useMilestoneAnalysisAdminEnabled } from "@/hooks/auth/use-permissions-gates";
import { getStageVariant } from "@/lib/badge-utils";
import { formatDate } from "@/lib/date-formatting";
import { MilestoneAnalysisCard } from "./milestone-analyses-card";

interface OpportunityCardProps {
  opportunity: {
    id: string;
    name: string;
    type?: string;
    stage_name: string;
    is_closed: boolean;
    is_won: boolean;
    created_date: string;
    close_date?: string;
  };
  taskGroups?: TaskGroup[];
  accountId?: string;
  onFilterCitedTouchpoints?: (taskId: string, touchpointIds: string[]) => void;
  onRequestAnalysis: (opportunityId: string) => void;
  requestMutation: ReturnType<typeof useCreateMilestoneAnalysisTask>;
  requestedTaskIds: Set<string>;
}

export function OpportunityCard({
  opportunity,
  taskGroups = [],
  onFilterCitedTouchpoints,
  onRequestAnalysis,
  requestMutation,
  requestedTaskIds,
}: OpportunityCardProps) {
  // Determine admin access
  const isAdmin = useMilestoneAnalysisAdminEnabled();

  // Helper to determine what content to show
  const getContentToRender = (): ReactNode => {
    // Check for failed tasks (they should be shown to admins)
    const failedTask =
      isAdmin &&
      taskGroups.find((group) => group.processing_status === "failed");

    if (failedTask) {
      return (
        <MilestoneAnalysisCard
          key={failedTask.task_id}
          taskGroup={failedTask}
          onFilterCitedTouchpoints={onFilterCitedTouchpoints}
        />
      );
    }

    // If there are completed analyses, show them
    const completedTaskGroups = taskGroups.filter((group) => {
      const hasCompletedAnalyses = group.analyses.length > 0;
      const showForCurrentUser =
        isAdmin || group.analyses.some((a) => a.is_approved);

      return hasCompletedAnalyses && showForCurrentUser;
    });

    if (completedTaskGroups.length > 0) {
      return (
        <>
          {completedTaskGroups.map((taskGroup) => (
            <MilestoneAnalysisCard
              key={taskGroup.task_id}
              taskGroup={taskGroup}
              onFilterCitedTouchpoints={onFilterCitedTouchpoints}
            />
          ))}
        </>
      );
    }

    // Check for other pending task types
    const pendingTaskGroup = taskGroups.find(
      (group) =>
        group.processing_status === "processing" ||
        // Check for pending status tasks
        group.processing_status === "pending",
    );

    if (pendingTaskGroup) {
      // For any pending status (in progress, pending, awaiting approval),
      // don't show content section - we'll just rely on the header buttons
      return null;
    }

    return null;
  };

  // We should show the request button unless one of these is true:
  // 1. There's already a pending task of any kind
  // 2. There are completed analyses
  // 3. There's a task that's just been requested
  const showRequestButton = !taskGroups.some(
    (group) =>
      group.processing_status === "processing" ||
      group.processing_status === "pending" ||
      group.analyses.length > 0 ||
      requestedTaskIds.has(group.task_id),
  );

  // Show processing status text like in the mockup
  const getStatusIndicator = () => {
    // Don't show status indicators if we're showing action buttons
    if (showRequestButton) return null;

    // Check for failed task first (highest priority)
    const failedTask = taskGroups.find(
      (group) => group.processing_status === "failed",
    );

    // Removing the FAILED status indicator from the header as requested
    if (failedTask && isAdmin) {
      return null;
    }

    const processingTask = taskGroups.find(
      (group) =>
        group.processing_status === "processing" ||
        group.processing_status === "pending",
    );

    if (processingTask) {
      return (
        <span className="text-xs text-muted-foreground flex items-center">
          <Clock className="h-3 w-3 mr-1" />
          ANALYSIS IN PROGRESS
        </span>
      );
    }

    // Check if there are unapproved analyses that a non-admin user can't see
    const hasUnapprovedAnalyses = taskGroups.some(
      (group) =>
        !isAdmin &&
        group.analyses.length > 0 &&
        !group.analyses.some((a) => a.is_approved),
    );

    if (hasUnapprovedAnalyses) {
      return (
        <span className="text-xs text-muted-foreground flex items-center">
          <Clock className="h-3 w-3 mr-1" />
          AWAITING PUBLICATION
        </span>
      );
    }

    return null;
  };

  return (
    <Card className="w-full overflow-hidden mb-3">
      <CardHeader className="px-6 py-4 bg-card/75 border-b border-border/40">
        {/* Opportunity title, badges, and date range */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="text-base font-medium">{opportunity.name}</div>

            {/* Type and stage badges */}
            <div className="flex items-center gap-1.5">
              {opportunity.type && (
                <Badge variant="secondary" className="text-xs">
                  {opportunity.type}
                </Badge>
              )}

              <Badge
                variant={getStageVariant(
                  opportunity.is_closed,
                  opportunity.is_won,
                )}
                className="text-xs"
              >
                {opportunity.stage_name}
              </Badge>
            </div>
          </div>

          {/* Right side with status, buttons, and date */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-4">
              {/* Status indicator (shown only when no buttons are displayed) */}
              {getStatusIndicator()}
            </div>

            {/* CTA Buttons - directly in the header */}
            {showRequestButton && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onRequestAnalysis(opportunity.id)}
                disabled={requestMutation.isPending}
              >
                {requestMutation.isPending &&
                requestMutation.variables?.opportunity_id === opportunity.id ? (
                  "Requesting..."
                ) : (
                  <>
                    <Plus className="h-4 w-4 mr-1" />
                    Request Opportunity Activation analysis
                  </>
                )}
              </Button>
            )}

            {/* Date range */}
            <div className="flex items-center text-sm text-muted-foreground">
              <Calendar className="h-4 w-4 mr-1" />
              <span className="whitespace-nowrap">
                {formatDate(opportunity.created_date)}
                {opportunity.close_date && (
                  <> - {formatDate(opportunity.close_date)}</>
                )}
              </span>
            </div>
          </div>
        </div>
      </CardHeader>

      {/* Render CardContent only if there's completed analysis content to show */}
      {(() => {
        const content = getContentToRender();
        return content && <CardContent className="p-0">{content}</CardContent>;
      })()}
    </Card>
  );
}
